import {Card, Modal, Spinner} from 'react-bootstrap'
import {useEffect, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import {FormattedMessage} from 'react-intl'
import {GET_CARRIER_POLICIES_DISTRIBUTION} from '../../../gql/queries/reportsQuery'
import {IResCarriers} from '../../../types/carriers'
import {parseId} from '../../../helpers'

interface ICarrierPoliciesData {
  carrierName: string
  policiesCount: number
  percentage: number
}

interface IResCarrierPolicies {
  carrierPoliciesDistribution: ICarrierPoliciesData[]
}

interface ICarrierPoliciesTableProps {
  dateFrom: string
  dateTo: string
  dateCriteria: 'submitted' | 'effective'
  carrier?: string
  stateEu?: string
  policyType?: string
  status?: string[]
  agent?: string[]
  paymentStatus?: string[]
  advisorPaid?: boolean
  loading?: boolean
  dataCarriers?: IResCarriers
  nonCanceledStatusCodes?: string[]
  onOpenPolicies?: (extraFilters: Record<string, unknown>) => void
  refreshTrigger?: number
}

type SortField = 'carrier' | 'policies'
type SortOrder = 'asc' | 'desc'

export const CarrierPoliciesTable = ({
  dateFrom,
  dateTo,
  dateCriteria,
  carrier,
  stateEu,
  policyType,
  status,
  agent,
  paymentStatus,
  advisorPaid,
  loading: parentLoading,
  dataCarriers,
  nonCanceledStatusCodes,
  onOpenPolicies,
  refreshTrigger,
}: ICarrierPoliciesTableProps) => {
  const getCarrierIdByName = (carrierName: string) =>
    dataCarriers?.allInsuranceOperators.edges.find((e) => e.node.name === carrierName)?.node.id

  const openPoliciesForCarrier = (carrierName: string) => {
    if (!onOpenPolicies) return
    const carrierId = getCarrierIdByName(carrierName)
    if (!carrierId) return
    onOpenPolicies({
      carrier: [parseId(carrierId).toString()],
      status: status && status.length > 0 ? status : nonCanceledStatusCodes,
    })
  }
  const [showModal, setShowModal] = useState(false)
  const [sortField, setSortField] = useState<SortField>('policies')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  const [getCarrierData, {data, loading}] = useLazyQuery<IResCarrierPolicies>(
    GET_CARRIER_POLICIES_DISTRIBUTION,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [getAllCarrierData, {data: fullData, loading: loadingFull}] =
    useLazyQuery<IResCarrierPolicies>(GET_CARRIER_POLICIES_DISTRIBUTION, {
      fetchPolicy: 'no-cache',
    })

  useEffect(() => {
    getCarrierData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 10,
      },
    })
  }, [dateFrom, dateTo, dateCriteria, carrier, stateEu, policyType, status, agent, paymentStatus, advisorPaid, refreshTrigger, getCarrierData])

  const handleShowMore = () => {
    getAllCarrierData({
      variables: {
        dateFrom,
        dateTo,
        dateCriteria,
        carrier: carrier ? [carrier] : undefined,
        stateEu: stateEu ? [stateEu] : undefined,
        policyType: policyType ? [policyType] : undefined,
        status: status,
        agent: agent,
        paymentStatus: paymentStatus,
        advisorPaid: advisorPaid,
        limit: 100,
      },
    })
    setShowModal(true)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder(field === 'policies' ? 'desc' : 'asc')
    }
  }

  const carrierData = data?.carrierPoliciesDistribution || []
  const fullCarrierData = fullData?.carrierPoliciesDistribution || []

  const sortedFullCarrierData = [...fullCarrierData].sort((a, b) => {
    if (sortField === 'carrier') {
      const comparison = a.carrierName.localeCompare(b.carrierName)
      return sortOrder === 'asc' ? comparison : -comparison
    } else {
      const comparison = a.policiesCount - b.policiesCount
      return sortOrder === 'asc' ? comparison : -comparison
    }
  })

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortOrder === 'asc' ? (
      <i className='bi bi-arrow-up ms-1'></i>
    ) : (
      <i className='bi bi-arrow-down ms-1'></i>
    )
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const formatPercent = (num: number, digits = 2) => `${num.toFixed(digits)}%`

  return (
    <>
      <Card className='card-flush h-100'>
        <Card.Header className='border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              <FormattedMessage
                id='dashboard.policies.carrierPolicies.title'
                defaultMessage='Policies per carrier (Top 10)'
              />
            </span>
            <span className='text-muted fw-semibold fs-7'>
              <FormattedMessage
                id='dashboard.policies.carrierPolicies.subtitle'
                defaultMessage='This is how your policies are segmented by insurance company'
              />
            </span>
          </h3>
          <div className='card-toolbar'>
            <button
              className='btn btn-sm btn-light-primary'
              onClick={handleShowMore}
              disabled={loading || parentLoading}
            >
              <i className='bi bi-plus-lg me-1'></i>
              <FormattedMessage id='dashboard.policies.common.seeMore' defaultMessage='See more' />
            </button>
          </div>
        </Card.Header>
      <Card.Body className='pt-0'>
        {(loading || parentLoading) && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && !parentLoading && carrierData.length === 0 && (
          <div className='d-flex justify-content-center align-items-center' style={{minHeight: 350}}>
            <p className='text-muted'>
              <FormattedMessage
                id='dashboard.policies.common.noDataPeriod'
                defaultMessage='No data available for the selected period'
              />
            </p>
          </div>
        )}
        {!loading && !parentLoading && carrierData.length > 0 && (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='ps-0' style={{width: '5%'}}></th>
                  <th style={{width: '50%'}}>
                    <FormattedMessage
                      id='dashboard.policies.common.insuranceCompany'
                      defaultMessage='Insurance company'
                    />
                  </th>
                  <th className='text-center' style={{width: '25%'}}>
                    <FormattedMessage
                      id='dashboard.policies.common.policies'
                      defaultMessage='Policies'
                    />
                  </th>
                  <th className='text-center pe-0' style={{width: '20%'}}>
                    <FormattedMessage
                      id='dashboard.policies.common.percentage'
                      defaultMessage='Percentage'
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {carrierData.map((entry, index) => (
                  <tr key={entry.carrierName}>
                    <td className='ps-0 text-center'>
                      <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div
                          className='rounded-circle bg-light-primary d-flex align-items-center justify-content-center me-3'
                          style={{width: 35, height: 35}}
                        >
                          <i className='bi bi-shield-check text-primary fs-5'></i>
                        </div>
                        <span className='text-gray-800 fw-bold fs-6'>{entry.carrierName}</span>
                      </div>
                    </td>
                    <td className='text-center'>
                      <span
                        className='fw-bold text-gray-800 fs-6'
                        style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                        onClick={() => openPoliciesForCarrier(entry.carrierName)}
                      >
                        {formatNumber(entry.policiesCount)}
                      </span>
                    </td>
                    <td className='text-center pe-0'>
                      <span className='badge badge-light-success fs-7'>
                        {formatPercent(entry.percentage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>

      {/* Modal for all carriers */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage
              id='dashboard.policies.carrierPolicies.modalTitle'
              defaultMessage='Policies per carrier'
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFull && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: 200}}>
              <Spinner animation='border' />
            </div>
          )}
          {!loadingFull && fullCarrierData.length === 0 && (
            <div className='text-center text-muted py-5'>
              <FormattedMessage
                id='dashboard.policies.common.noData'
                defaultMessage='No data available'
              />
            </div>
          )}
          {!loadingFull && fullCarrierData.length > 0 && (
            <div className='table-responsive' style={{maxHeight: '60vh', overflowY: 'auto'}}>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr className='fw-bold text-muted'>
                    <th className='ps-0 text-center' style={{width: '5%'}}></th>
                    <th
                      style={{width: '50%', cursor: 'pointer'}}
                      onClick={() => handleSort('carrier')}
                    >
                      <FormattedMessage
                        id='dashboard.policies.common.insuranceCompany'
                        defaultMessage='Insurance company'
                      />
                      {getSortIcon('carrier')}
                    </th>
                    <th
                      className='text-center'
                      style={{width: '25%', cursor: 'pointer'}}
                      onClick={() => handleSort('policies')}
                    >
                      <FormattedMessage
                        id='dashboard.policies.common.policies'
                        defaultMessage='Policies'
                      />
                      {getSortIcon('policies')}
                    </th>
                    <th className='text-center pe-0' style={{width: '20%'}}>
                      <FormattedMessage
                        id='dashboard.policies.common.percentage'
                        defaultMessage='Percentage'
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFullCarrierData.map((entry, index) => (
                    <tr key={entry.carrierName}>
                      <td className='ps-0 text-center'>
                        <span className='text-muted fw-bold fs-7'>{index + 1}</span>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div
                            className='rounded-circle bg-light-primary d-flex align-items-center justify-content-center me-3'
                            style={{width: 35, height: 35}}
                          >
                            <i className='bi bi-shield-check text-primary fs-5'></i>
                          </div>
                          <span className='text-gray-800 fw-bold fs-6'>{entry.carrierName}</span>
                        </div>
                      </td>
                      <td className='text-center'>
                        <span
                          className='fw-bold text-gray-800 fs-6'
                          style={onOpenPolicies ? {cursor: 'pointer', textDecoration: 'underline dotted'} : undefined}
                          onClick={() => openPoliciesForCarrier(entry.carrierName)}
                        >
                          {formatNumber(entry.policiesCount)}
                        </span>
                      </td>
                      <td className='text-center pe-0'>
                        <span className='badge badge-light-success fs-7'>
                          {formatPercent(entry.percentage)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary' onClick={() => setShowModal(false)}>
            <FormattedMessage id='common.action.close' defaultMessage='Close' />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
